import { useCallback, useEffect, useState } from "react";
import { api, apiError, tanggal } from "@/lib/api";
import { ErpLayout, Card } from "@/erp/shell";
import { toast } from "sonner";
import { FileDown, RefreshCw, Users, BedDouble, FileText, TableProperties } from "lucide-react";

const inp = "w-full rounded-lg border border-[#E5DEC9] px-3 py-2 text-sm outline-none focus:border-[#C5A028]";

const TABS = [
  { key: "manifest", label: "Manifest Keberangkatan", icon: Users },
  { key: "roomlist", label: "Roomlist Kamar", icon: BedDouble },
  { key: "dokumen", label: "Progres Dokumen", icon: FileText },
];

const STATUS_BADGE = {
  approved: "bg-[#F5F1E8] text-[#15803D]",
  submitted: "bg-[#EEF2FF] text-[#3730A3]",
  under_review: "bg-[#EEF2FF] text-[#3730A3]",
  revision_required: "bg-[#FBE9E9] text-[#B91C1C]",
  rejected: "bg-[#FBE9E9] text-[#B91C1C]",
  not_uploaded: "bg-[#F4EFE6] text-[#8A867C]",
};

function DocStatusBadge({ s }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_BADGE[s] || STATUS_BADGE.not_uploaded}`}>
      {String(s || "not_uploaded").replace(/_/g, " ")}
    </span>
  );
}

function PctBar({ pct }) {
  const color = pct >= 80 ? "#15803D" : pct >= 40 ? "#B45309" : "#B91C1C";
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-20 overflow-hidden rounded-full bg-[#E5DEC9]">
        <div style={{ width: `${pct}%`, background: color }} className="h-full rounded-full transition-all" />
      </div>
      <span className="text-xs font-semibold" style={{ color }}>{pct}%</span>
    </div>
  );
}

export function LaporanOperasional() {
  const [tab, setTab] = useState("manifest");
  const [departures, setDepartures] = useState([]);
  const [selectedDep, setSelectedDep] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadDepartures = useCallback(() => {
    api.get("/reports/departures").then((r) => {
      setDepartures(r.data);
      if (r.data.length > 0 && !selectedDep) {
        setSelectedDep(`${r.data[0].depart_date}|${r.data[0].product_id}`);
      }
    }).catch(() => {});
  }, [selectedDep]);

  useEffect(() => { loadDepartures(); }, [loadDepartures]);

  const selectedInfo = departures.find(
    (d) => `${d.depart_date}|${d.product_id}` === selectedDep
  );

  const loadReport = useCallback(async () => {
    if (!selectedDep) return;
    const [depart_date, product_id] = selectedDep.split("|");
    const endpoint = tab === "manifest" ? "/reports/manifest"
      : tab === "roomlist" ? "/reports/roomlist"
      : "/reports/document-progress";
    setLoading(true);
    setData(null);
    try {
      const { data: d } = await api.get(endpoint, { params: { depart_date, product_id } });
      setData(d);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [selectedDep, tab]);

  useEffect(() => { if (selectedDep) loadReport(); }, [loadReport, selectedDep, tab]);

  const exportPdf = async () => {
    if (!selectedDep) return;
    const [depart_date, product_id] = selectedDep.split("|");
    const endpoint = tab === "manifest" ? "/reports/manifest/pdf"
      : tab === "roomlist" ? "/reports/roomlist/pdf"
      : "/reports/document-progress/pdf";
    try {
      const res = await api.get(endpoint, { params: { depart_date, product_id }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };

  const exportXlsx = async () => {
    if (!selectedDep) return;
    const [depart_date, product_id] = selectedDep.split("|");
    const endpoint = tab === "manifest" ? "/reports/manifest/xlsx"
      : tab === "roomlist" ? "/reports/roomlist/xlsx"
      : "/reports/document-progress/xlsx";
    const label = tab === "manifest" ? "Manifest"
      : tab === "roomlist" ? "Roomlist"
      : "ProgresDoc";
    try {
      const res = await api.get(endpoint, { params: { depart_date, product_id }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${label}_${depart_date}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <ErpLayout title="Laporan Operasional" breadcrumb={["Laporan Operasional"]}>
      {/* Filter Bar */}
      <Card className="mb-5">
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-[280px] flex-1">
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-[#4A4844]">
              Tanggal Keberangkatan
            </label>
            <select
              className={inp}
              value={selectedDep}
              onChange={(e) => setSelectedDep(e.target.value)}
              data-testid="laporan-dep-select"
            >
              <option value="">Pilih keberangkatan...</option>
              {departures.map((d) => (
                <option key={`${d.depart_date}|${d.product_id}`} value={`${d.depart_date}|${d.product_id}`}>
                  {tanggal(d.depart_date)} — {d.product_name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadReport} disabled={loading || !selectedDep}
              className="flex items-center gap-2 rounded-xl border border-[#E5DEC9] bg-white px-4 py-2.5 text-sm font-semibold hover:bg-[#F4EFE6] disabled:opacity-40"
              data-testid="laporan-refresh-btn">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Muat Ulang
            </button>
            <button onClick={exportPdf} disabled={!data}
              className="btn-gold flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold disabled:opacity-40"
              data-testid="laporan-pdf-btn">
              <FileDown size={14} /> Export PDF
            </button>
            <button onClick={exportXlsx} disabled={!data}
              className="flex items-center gap-2 rounded-xl border border-[#15803D] bg-white px-4 py-2.5 text-sm font-bold text-[#15803D] hover:bg-[#F0FDF4] disabled:opacity-40 transition-colors"
              data-testid="laporan-xlsx-btn">
              <TableProperties size={14} /> Export Excel
            </button>
          </div>
        </div>

        {/* Summary info */}
        {data && (
          <div className="mt-4 flex flex-wrap gap-4 rounded-xl bg-[#F4EFE6] px-4 py-3">
            <div className="text-sm"><span className="text-[#4A4844]">Paket: </span><b>{data.product_name}</b></div>
            <div className="text-sm"><span className="text-[#4A4844]">Keberangkatan: </span><b>{tanggal(data.depart_date)}</b></div>
            <div className="text-sm"><span className="text-[#4A4844]">Total Jamaah: </span><b className="text-[#C5A028]">{data.total_pilgrims}</b></div>
            {tab === "manifest" && <div className="text-sm"><span className="text-[#4A4844]">Total Booking: </span><b>{data.total_bookings}</b></div>}
          </div>
        )}
      </Card>

      {/* Tab Navigation */}
      <div className="mb-5 flex gap-2 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`laporan-tab-${t.key}`}
            className={`flex shrink-0 items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold transition-colors
              ${tab === t.key ? "bg-[#141414] text-white" : "border border-[#E5DEC9] bg-white text-[#4A4844] hover:bg-[#F4EFE6]"}`}>
            <t.icon size={15} />{t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading && (
        <Card className="py-16 text-center text-sm text-[#8A867C]">
          <RefreshCw size={24} className="mx-auto mb-3 animate-spin text-[#C5A028]" />
          Memuat data laporan...
        </Card>
      )}

      {!loading && !data && (
        <Card className="py-16 text-center">
          <Users size={32} className="mx-auto mb-3 text-[#C5A028]" />
          <p className="text-sm text-[#8A867C]">Pilih tanggal keberangkatan untuk menampilkan laporan.</p>
        </Card>
      )}

      {!loading && data && tab === "manifest" && <ManifestTable data={data} />}
      {!loading && data && tab === "roomlist" && <RoomlistTable data={data} />}
      {!loading && data && tab === "dokumen" && <DocProgressTable data={data} />}
    </ErpLayout>
  );
}

function ManifestTable({ data }) {
  const { pilgrims } = data;
  if (!pilgrims?.length) return <Card className="py-8 text-center text-sm text-[#8A867C]">Tidak ada data jamaah.</Card>;
  return (
    <Card data-testid="manifest-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#141414] text-left text-xs uppercase text-white">
            <tr>
              {["No", "Nama Sesuai Paspor", "NIK", "No. Paspor", "Exp. Paspor", "L/P", "Tgl Lahir", "Kamar", "Booking", "Status Dok"].map((h) => (
                <th key={h} className="px-3 py-3 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pilgrims.map((p, i) => (
              <tr key={p.id} className={`border-t border-[#F4EFE6] ${i % 2 === 1 ? "bg-[#FAF7F0]" : ""}`}
                data-testid={`manifest-row-${p.id}`}>
                <td className="px-3 py-2.5 text-center font-semibold text-[#8A867C]">{p.no}</td>
                <td className="px-3 py-2.5 font-semibold">{p.full_name_passport || p.full_name}</td>
                <td className="px-3 py-2.5 font-mono text-xs">{p.nik || "-"}</td>
                <td className="px-3 py-2.5 font-mono text-xs">{p.passport_number || "-"}</td>
                <td className="px-3 py-2.5 text-xs">{p.passport_expiry || "-"}</td>
                <td className="px-3 py-2.5 text-center">{p.gender || "-"}</td>
                <td className="px-3 py-2.5 text-xs">{p.birth_date || "-"}</td>
                <td className="px-3 py-2.5">
                  <span className="rounded-full bg-[#F4EFE6] px-2 py-0.5 text-xs font-semibold text-[#8C6D1F]">{p.room_type}</span>
                </td>
                <td className="px-3 py-2.5 text-xs font-semibold">{p.booking_code}</td>
                <td className="px-3 py-2.5"><DocStatusBadge s={p.document_status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 text-xs text-[#8A867C]">Total {pilgrims.length} jamaah</div>
    </Card>
  );
}

function RoomlistTable({ data }) {
  const { rooms, summary } = data;
  if (!rooms || Object.keys(rooms).length === 0) return <Card className="py-8 text-center text-sm text-[#8A867C]">Tidak ada data kamar.</Card>;

  const roomOrder = ["Quad", "Triple", "Double", "Single"];
  const sortedRooms = Object.keys(rooms).sort((a, b) =>
    (roomOrder.indexOf(a) + 99) - (roomOrder.indexOf(b) + 99)
  );

  return (
    <div className="space-y-6" data-testid="roomlist-tables">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {sortedRooms.map((rt) => (
          <Card key={rt} className="text-center">
            <div className="eyebrow">{rt}</div>
            <div className="mt-2 font-display text-3xl font-bold text-[#C5A028]">{summary[rt]}</div>
            <div className="text-xs text-[#8A867C]">jamaah</div>
          </Card>
        ))}
      </div>

      {/* Detail per room type */}
      {sortedRooms.map((rt) => (
        <Card key={rt}>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-display text-base font-bold text-[#141414]">Kamar {rt}</h3>
            <span className="rounded-full bg-[#F4EFE6] px-3 py-1 text-xs font-semibold text-[#8C6D1F]">{rooms[rt].length} jamaah</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid={`roomlist-${rt}`}>
              <thead className="bg-[#141414] text-left text-xs uppercase text-white">
                <tr>
                  {["No", "Nama", "L/P", "Tgl Lahir", "No. Paspor", "Booking", "No. HP"].map((h) => (
                    <th key={h} className="px-3 py-2.5 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rooms[rt].map((p, i) => (
                  <tr key={p.id} className={`border-t border-[#F4EFE6] ${i % 2 === 1 ? "bg-[#FAF7F0]" : ""}`}>
                    <td className="px-3 py-2.5 text-center text-[#8A867C]">{i + 1}</td>
                    <td className="px-3 py-2.5 font-semibold">{p.full_name_passport || p.full_name}</td>
                    <td className="px-3 py-2.5 text-center">{p.gender || "-"}</td>
                    <td className="px-3 py-2.5 text-xs">{p.birth_date || "-"}</td>
                    <td className="px-3 py-2.5 font-mono text-xs">{p.passport_number || "-"}</td>
                    <td className="px-3 py-2.5 text-xs font-semibold">{p.booking_code}</td>
                    <td className="px-3 py-2.5 text-xs">{p.phone || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}
    </div>
  );
}

function DocProgressTable({ data }) {
  const { pilgrims, doc_types } = data;
  if (!pilgrims?.length) return <Card className="py-8 text-center text-sm text-[#8A867C]">Tidak ada data jamaah.</Card>;

  const completed = pilgrims.filter((p) => p.completion_pct === 100).length;
  const partial = pilgrims.filter((p) => p.completion_pct > 0 && p.completion_pct < 100).length;
  const none = pilgrims.filter((p) => p.completion_pct === 0).length;

  return (
    <div className="space-y-5" data-testid="docprogress-table">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="text-center"><div className="eyebrow">Lengkap (100%)</div><div className="mt-2 font-display text-2xl font-bold text-[#15803D]">{completed}</div></Card>
        <Card className="text-center"><div className="eyebrow">Sebagian</div><div className="mt-2 font-display text-2xl font-bold text-[#B45309]">{partial}</div></Card>
        <Card className="text-center"><div className="eyebrow">Belum Upload</div><div className="mt-2 font-display text-2xl font-bold text-[#B91C1C]">{none}</div></Card>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#141414] text-left text-xs text-white">
              <tr>
                <th className="px-3 py-3 uppercase">No</th>
                <th className="px-3 py-3 uppercase">Nama</th>
                <th className="px-3 py-3 uppercase">Booking</th>
                {(doc_types || []).map((dt) => (
                  <th key={dt.id} className="px-3 py-3 text-center text-[10px]">{dt.name.substring(0, 14)}</th>
                ))}
                <th className="px-3 py-3 uppercase">Progress</th>
              </tr>
            </thead>
            <tbody>
              {pilgrims.map((p, i) => (
                <tr key={p.pilgrim_id} className={`border-t border-[#F4EFE6] ${i % 2 === 1 ? "bg-[#FAF7F0]" : ""}`}
                  data-testid={`docprogress-row-${p.pilgrim_id}`}>
                  <td className="px-3 py-2.5 text-center text-[#8A867C]">{p.no}</td>
                  <td className="px-3 py-2.5 font-semibold">{p.full_name}</td>
                  <td className="px-3 py-2.5 text-xs font-semibold">{p.booking_code}</td>
                  {(p.types || []).map((t) => (
                    <td key={t.type_id} className="px-3 py-2.5 text-center">
                      {t.status === "approved" ? (
                        <span className="text-[#15803D] font-bold">✓</span>
                      ) : t.status === "submitted" || t.status === "under_review" ? (
                        <span className="text-[#3730A3]">~</span>
                      ) : t.status === "revision_required" ? (
                        <span className="text-[#B91C1C] font-bold">!</span>
                      ) : (
                        <span className="text-[#C7C0B2]">−</span>
                      )}
                    </td>
                  ))}
                  <td className="px-3 py-2.5"><PctBar pct={p.completion_pct} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex flex-wrap gap-4 text-xs text-[#4A4844]">
          <span><b className="text-[#15803D]">✓</b> = Disetujui</span>
          <span><b className="text-[#3730A3]">~</b> = Proses Review</span>
          <span><b className="text-[#B91C1C]">!</b> = Revisi Diperlukan</span>
          <span><b className="text-[#C7C0B2]">−</b> = Belum Upload</span>
        </div>
      </Card>
    </div>
  );
}
